import { useCallback, useState } from "react";
import { useMutation } from "react-query";
import { useLocation } from "react-router-dom";
import { BudgetService } from "../../../../services/budget.service";
import ClientSpreadsheetService from "../../../../services/clientSpreadsheet.service";
import {
  clientSpreadsheetStageFor,
  describeClientSpreadsheetExport,
} from "../../../../types/clientSpreadsheet.type";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { TabActive } from "../../../../types/project.type";

const ExportType = {
  CLIENT: "CLIENT",
} as const;

const ExportMode = {
  SIMPLIFIED: "SIMPLIFIED",
} as const;

const ExportSortOrder = {
  CREATION_ORDER: "CREATION_ORDER",
} as const;

/** #MMV-2582: "Por Fornecedor" (estrutura atual) vs. "Por Agrupamento" (nova). */
export enum ExportView {
  BY_SUPPLIER = "BY_SUPPLIER",
  BY_GROUP = "BY_GROUP",
}

function downloadSpreadsheet(data: BlobPart, fileName: string) {
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.setAttribute("download", fileName);
  link.href = URL.createObjectURL(blob);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function useBudgetExport(version: number) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId");
  const { activeBudget, budgetList, showNotification, runBudgetAutosave } =
    useBudget();
  const { tabActive, project } = useProject();
  const [isExporting, setIsExporting] = useState(false);

  const buildFileName = useCallback(
    (suffix: string) => {
      if (project?.mmCode) {
        return `MM${project.mmCode} - ${project.event?.name} - ${suffix}.xlsx`;
      }
      return "PrevisaodeCustos.xlsx";
    },
    [project]
  );

  const tryClientTemplate = useCallback(
    async (budgetIds: string[]): Promise<boolean> => {
      const clientId = project?.client?._id;
      if (!clientId || !projectId) return false;

      setIsExporting(true);
      try {
        const meta = await ClientSpreadsheetService.generate(clientId, {
          projectId,
          budgetIds: budgetIds.length > 0 ? budgetIds : undefined,
          ...clientSpreadsheetStageFor(tabActive, version),
        });
        const { message, severity } = describeClientSpreadsheetExport(meta);
        showNotification(message, severity);
        return true;
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) {
          return false;
        }
        if (status === 400) {
          const body = err?.response?.data;
          showNotification(
            body?.message || "Erro ao gerar planilha do cliente.",
            "warning"
          );
          return true;
        }
        showNotification(
          "Nao foi possivel gerar a planilha do cliente.",
          "warning"
        );
        return true;
      } finally {
        setIsExporting(false);
      }
    },
    [project?.client?._id, projectId, showNotification, tabActive, version]
  );

  const { mutateAsync: exportBudgetXlx } = useMutation((data: any) =>
    BudgetService.exportXLX(data)
  );
  const { mutateAsync: exportNegotiationXlx } = useMutation((data: any) =>
    BudgetService.exportNegotiationXLX(data)
  );
  const { mutateAsync: exportApprovedXlx } = useMutation((data: any) =>
    BudgetService.exportApprovedXLX(data)
  );

  const exportCurrentBudget = useCallback(
    async (view: ExportView = ExportView.BY_SUPPLIER) => {
      if (!activeBudget?._id || !projectId) return;

      // Export reads persisted server data, not local React state. Flush any
      // pending tax/exchange recalc so the spreadsheet matches the editor.
      await runBudgetAutosave("manual");

      const budgetName =
        activeBudget.name ||
        budgetList?.find((el) => el._id === activeBudget._id)?.name ||
        `Orçamento ${(activeBudget.budgetIndex ?? 0) + 1}`;

      // A negociação exportada é a versão aberta na aba -- sem ela não há o
      // que pedir, nem ao template do cliente nem à exportação MM.
      if (tabActive === TabActive.NEGOTIATION && version <= 0) {
        showNotification(
          "Selecione uma versão de negociação para exportar.",
          "warning"
        );
        return;
      }

      // #MMV-2582: template do cliente não conhece a visão por agrupamento
      // (estrutura própria do template) -- só tenta o template na visão
      // padrão; "Por Agrupamento" sempre usa a exportação MM.
      const usedTemplate =
        view === ExportView.BY_SUPPLIER
          ? await tryClientTemplate([activeBudget._id])
          : false;
      if (usedTemplate) return;

      setIsExporting(true);
      try {
        if (tabActive === TabActive.BUDGET) {
          const data = await exportBudgetXlx({
            ids: activeBudget._id,
            exportType: ExportType.CLIENT,
            sortOrder: ExportSortOrder.CREATION_ORDER,
            view,
          });
          downloadSpreadsheet(data, buildFileName(budgetName));
          showNotification("Exportado com sucesso!", "success");
          return;
        }

        if (tabActive === TabActive.NEGOTIATION) {
          const data = await exportNegotiationXlx({
            exportType: ExportType.CLIENT,
            exportMode: ExportMode.SIMPLIFIED,
            sortOrder: ExportSortOrder.CREATION_ORDER,
            view,
            budget: {
              budgetId: activeBudget._id,
              negotiationVersion: version,
            },
          });
          downloadSpreadsheet(data, buildFileName("Negociação"));
          showNotification("Exportado com sucesso!", "success");
          return;
        }

        if (tabActive === TabActive.APPROVED) {
          const data = await exportApprovedXlx({
            exportType: ExportType.CLIENT,
            exportMode: ExportMode.SIMPLIFIED,
            sortOrder: ExportSortOrder.CREATION_ORDER,
            view,
            budgetId: activeBudget._id,
          });
          downloadSpreadsheet(data, buildFileName("Fechamento"));
          showNotification("Exportado com sucesso!", "success");
        }
      } catch {
        showNotification("Não foi possível exportar", "warning");
      } finally {
        setIsExporting(false);
      }
    },
    [
      activeBudget,
      budgetList,
      buildFileName,
      exportApprovedXlx,
      exportBudgetXlx,
      exportNegotiationXlx,
      projectId,
      runBudgetAutosave,
      showNotification,
      tabActive,
      tryClientTemplate,
      version,
    ]
  );

  return { exportCurrentBudget, isExporting };
}
